import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Inline,
  PageHeader,
  Skeleton,
  Switch,
  buttonClassName,
} from "@evolua/ui";
import { useState } from "react";
import type { InboxEventType } from "@evolua/api-client";
import { QueryErrorState } from "../api/QueryErrorState.js";
import {
  useMarkAllMeNotificationsRead,
  useMarkMeNotificationRead,
  useMeInbox,
  useMeNotificationPreferences,
  useMePushDeliveries,
  usePutMeNotificationPreferences,
} from "../api/use-inbox.js";

const CHANNELS = ["in_app", "push", "email"] as const;
const CHANNEL_LABELS: Record<(typeof CHANNELS)[number], string> = {
  in_app: "In-app",
  push: "Push",
  email: "E-mail",
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PreferencesSection() {
  const query = useMeNotificationPreferences();
  const put = usePutMeNotificationPreferences();
  const push = useMePushDeliveries();
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  if (query.isPending) return <Skeleton height="8rem" label="Preferências" />;
  if (query.isError || !query.data) {
    return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const { preferences, labels } = query.data;
  const types = Object.keys(preferences) as InboxEventType[];

  async function toggle(
    type: InboxEventType,
    channel: (typeof CHANNELS)[number],
    value: boolean,
  ) {
    setSaveMsg(null);
    await put.mutateAsync({
      preferences: { [type]: { [channel]: value } },
    });
    setSaveMsg("Preferências salvas (ownership da plataforma — sobrevivem ao sync ERP).");
  }

  return (
    <section className="evolua-stack evolua-stack--3" aria-labelledby="prefs-title">
      <h2 id="prefs-title" className="evolua-section-title">
        Preferências
      </h2>
      <p className="evolua-page-lead">
        Escolha canais por tipo de evento. Alertas de segurança não desligam in-app nem
        e-mail.
      </p>
      <div className="evolua-stack evolua-stack--3">
        {types.map((type) => {
          const pref = preferences[type]!;
          const locked = type === "security.alert";
          return (
            <Card key={type} title={labels[type] ?? type} padding="sm">
              <div className="evolua-stack evolua-stack--3">
                {CHANNELS.map((ch) => {
                  const disabled =
                    put.isPending || (locked && (ch === "in_app" || ch === "email"));
                  return (
                    <Switch
                      key={ch}
                      label={CHANNEL_LABELS[ch]}
                      checked={pref[ch]}
                      disabled={disabled}
                      onChange={(e) => void toggle(type, ch, e.target.checked)}
                    />
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>
      {saveMsg ? <Alert tone="success">{saveMsg}</Alert> : null}
      {push.data && push.data.data.length > 0 ? (
        <p className="evolua-muted">
          Push mock: {push.data.data.length} entrega(s) enfileirada(s) (FASE-09-T04 =
          VAPID real).
        </p>
      ) : null}
    </section>
  );
}

/** Central de notificações in-app (FASE-06-T40). */
export function NotificacoesPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const query = useMeInbox(unreadOnly ? { unreadOnly: true } : {});
  const markRead = useMarkMeNotificationRead();
  const markAll = useMarkAllMeNotificationsRead();

  return (
    <>
      <PageHeader
        title="Notificações"
        lead="Avisos de férias, holerite e segurança. Preferências por tipo — ownership da plataforma."
      />

      <div className="evolua-stack evolua-stack--3">
        <Button
          type="button"
          size="sm"
          variant={unreadOnly ? "primary" : "secondary"}
          onClick={() => setUnreadOnly((v) => !v)}
        >
          {unreadOnly ? "Mostrando não lidas" : "Filtrar não lidas"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={markAll.isPending || (query.data?.unreadCount ?? 0) === 0}
          onClick={() => void markAll.mutateAsync()}
        >
          Marcar todas como lidas
        </Button>
        {query.data ? (
          <Badge tone={query.data.unreadCount > 0 ? "warning" : "neutral"}>
            {query.data.unreadCount} não lida(s)
          </Badge>
        ) : null}
      </div>

      {query.isPending ? (
        <div className="evolua-stack evolua-stack--2" aria-busy="true">
          <Skeleton height="3.5rem" />
          <Skeleton height="3.5rem" />
        </div>
      ) : query.isError ? (
        <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.data.length === 0 ? (
        <EmptyState
          title="Caixa vazia"
          description={
            unreadOnly
              ? "Nenhuma notificação não lida."
              : "Você ainda não recebeu notificações."
          }
        />
      ) : (
        <ul className="evolua-stack evolua-stack--3">
          {query.data.data.map((n) => {
            const unread = n.readAt === null;
            return (
              <li key={n.id}>
                <Card
                  padding="sm"
                  title={n.title}
                  header={unread ? <Badge tone="warning">Nova</Badge> : undefined}
                  actions={
                    <Inline gap={2}>
                      {n.href ? (
                        <Link
                          to={n.href}
                          className={buttonClassName({
                            variant: "secondary",
                            size: "sm",
                          })}
                        >
                          Abrir
                        </Link>
                      ) : null}
                      {unread ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={markRead.isPending}
                          onClick={() => void markRead.mutateAsync(n.id)}
                        >
                          Marcar lida
                        </Button>
                      ) : null}
                    </Inline>
                  }
                >
                  <p>{n.body}</p>
                  <p className="evolua-muted">
                    {formatWhen(n.createdAt)}
                    {n.pushQueued ? " · push enfileirado" : ""}
                  </p>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <PreferencesSection />

      <p className="evolua-muted">
        <Badge tone="neutral">Mock-first</Badge> Push real (VAPID) = FASE-09-T04.
      </p>
    </>
  );
}
